import {colorSwatch} from '../utils/colorSwatch.js';
import {MenuPanels} from '../../models/UIModel.js';
import React, {useContext, useMemo, useState} from 'react';
import {MenuButton} from '../components/menu-button.js';
import {
    Column,
    Container,
    Panel,
    Panel9Slice,
    Row,
    Text,
} from '@wonderlandengine/react-ui/components';
import {
    Align,
    FlexDirection,
    Justify,
    PositionType,
    YogaNodeProps,
} from '@wonderlandengine/react-ui';
import {
    RotBMaterialContext,
    RotBMaterialContextValue,
    useRotBMaterialContext,
} from '../utils/rotb-material-context.js';
import {WindowHeader} from '../components/window-header.js';

import {Localization} from '../../utils/Localization.js';
import {Window} from '../components/window.js';
import {Sounds} from '../../enums/Sounds.js';
import {goToPreviousMenu} from '../utils/goToPreviousMenu.js';
import {parseColor} from '../utils/utils.js';
import {UIColorTextured} from '../../types/materials.js';
import {Material} from '@wonderlandengine/api';
import {StatNames} from '../../enums/StatNames.js';
import {Statistics} from '../../components/statistics.js';
import {formatNumber} from '../../utils/Utils.js';

type Stat = {
    name: string;
    value: string;
};

function displayLastModeStat(): Stat {
    const stat = StatNames.LastGameModePlayed;
    const lastGameModePlayed = Statistics.instance.getLong(StatNames.LastGameModePlayed);
    let statName = Localization.instance.get(StatNames[stat]);
    let value = '';
    switch (lastGameModePlayed) {
        case 0:
            value = Localization.instance.get('NormalModeStat');
            break;
        case 1:
            value = Localization.instance.get('TimedModeStat');
            break;
        case 2:
            value = Localization.instance.get('SkullModeStat');
            break;
        case 3:
            value = Localization.instance.get('TutorialModeStat');
            break;
        case 4:
            value = Localization.instance.get('EasyModeStat');
            break;
        case 5:
            value = Localization.instance.get('ZenModeStat');
            break;
        case 6:
            value = Localization.instance.get('QuickplayModeStat');
            break;
        case 7:
            value = Localization.instance.get('FungusModeStat');
            break;
        case 8:
            value = Localization.instance.get('CupcakeModeStat');
            break;
        default:
            value = Localization.instance.get('TutorialModeStat');
            break;
    }

    if (lastGameModePlayed === 0) {
        statName = Localization.instance.get('LastGameModePlayedNone');
    }

    return {
        name: statName,
        value,
    };
}

function displayStat(stat: StatNames): Stat {
    let statName = Localization.instance.get(StatNames[stat]);
    let value = Statistics.instance.getLong(stat);
    return {
        name: statName,
        value: formatNumber(value),
    };
}

function displayTimeStat(stat: StatNames): Stat {
    let statName = Localization.instance.get(StatNames[stat]);
    let statAmount = Statistics.instance.getLong(stat);
    let label = '';
    if (statAmount == 0) {
        label = '0';
    } else {
        let hours = 0;
        let minutes = 0;
        let seconds = 0;

        minutes = Math.floor(statAmount / 60);

        if (minutes > 59) {
            hours = Math.floor(minutes / 60);
            minutes = minutes % 60;
        }

        seconds = statAmount % 60;

        label = `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return {
        name: statName,
        value: label,
    };
}

const StatLine = (props: {stat: Stat; color: string}) => {
    const rotbMats = useContext(RotBMaterialContext);
    const [statName] = useState<string>(props.stat.name);
    const [statValue] = useState<string>(props.stat.value);

    return (
        <Panel
            backgroundColor={props.color}
            rounding={0}
            width="100%"
            height={38}
            alignContent={Align.Center}
            alignItems={Align.Center}
        >
            <Row
                margin={5}
                paddingLeft={5}
                paddingRight={5}
                width={450}
                height={38}
                alignItems={Align.Center}
                justifyContent={Justify.Center}
            >
                <Text
                    width={300}
                    fontSize={20}
                    color={colorSwatch.DescriptionText}
                    textAlign="left"
                    text={statName}
                    material={rotbMats.regularTextMaterial}
                ></Text>

                <Text
                    width={150}
                    fontSize={20}
                    color={colorSwatch.DescriptionText}
                    textAlign="right"
                    text={statValue}
                    material={rotbMats.regularTextMaterial}
                ></Text>
            </Row>
        </Panel>
    );
};

const StatHeader = (props: {titleKey: string}) => {
    return (
        <Row
            width="100%"
            height={40}
            alignItems={Align.Center}
            justifyContent={Justify.Center}
        >
            <Text
                fontSize={24}
                color={colorSwatch.DescriptionText}
                textAlign="center"
                text={Localization.instance.get(props.titleKey)}
            ></Text>
        </Row>
    );
};

export const StatsPanel = (props: React.PropsWithChildren<YogaNodeProps> & {}) => {
    const rotbMats = useRotBMaterialContext() as RotBMaterialContextValue;
    const gradientMaterial = useMemo<Material>(() => {
        const mat = rotbMats?.gradientMaterial.clone();
        (mat as UIColorTextured).setColor(parseColor(colorSwatch.StatsBackground));
        return mat;
    }, [props]);

    return (
        <Container
            position={PositionType.Absolute}
            width="100%"
            height="100%"
            flexDirection={FlexDirection.Row}
            justifyContent={Justify.Center}
        >
            <Column marginTop={36}>
                <Window>
                    <Column
                        height={720}
                        marginTop={20}
                        alignSelf={Align.Center}
                        width={500}
                        rowGap={20}
                    >
                        <WindowHeader title={Localization.instance.get('Stats')} />
                        <Panel
                            flexGrow={1}
                            rounding={0}
                            material={gradientMaterial}
                            backgroundColor="#000000"
                            flexDirection={FlexDirection.Column}
                            padding={10}
                            gap={4}
                        >
                            <StatHeader titleKey="LastGame" />
                            <StatLine
                                stat={displayLastModeStat()}
                                color="#2B5969"
                            ></StatLine>
                            <StatLine
                                stat={displayStat(StatNames.LastGameScore)}
                                color="#2B5969"
                            ></StatLine>
                            <StatLine
                                stat={displayStat(StatNames.LastGameCombo)}
                                color="#2B5969"
                            ></StatLine>
                            <StatLine
                                stat={displayStat(StatNames.LastGameLevel)}
                                color="#2B5969"
                            ></StatLine>
                            <StatLine
                                stat={displayStat(StatNames.LastShapeBonus)}
                                color="#2B5969"
                            ></StatLine>
                            <StatLine
                                stat={displayStat(StatNames.LastCupcakes)}
                                color="#2B5969"
                            ></StatLine>

                            <StatHeader titleKey="General" />
                            <StatLine
                                stat={displayStat(StatNames.GameRank)}
                                color="#2B5969"
                            ></StatLine>
                            <StatLine
                                stat={displayStat(StatNames.HighestLevel)}
                                color="#2B5969"
                            ></StatLine>
                            <StatLine
                                stat={displayStat(StatNames.HighestNumberInChain)}
                                color="#2B5969"
                            ></StatLine>
                            <StatLine
                                stat={displayStat(StatNames.HighCombo)}
                                color="#2B5969"
                            ></StatLine>
                            <StatLine
                                stat={displayStat(StatNames.HighShapeBonus)}
                                color="#2B5969"
                            ></StatLine>

                            <StatLine
                                stat={displayStat(StatNames.TotalGamesPlayed)}
                                color="#2B5969"
                            ></StatLine>
                            <StatLine
                                stat={displayTimeStat(StatNames.TotalPlayTime)}
                                color="#2B5969"
                            ></StatLine>
                        </Panel>
                    </Column>
                </Window>
                <Row justifyContent={Justify.SpaceAround}>
                    <MenuButton
                        width={250}
                        height={54}
                        margin={6}
                        color={colorSwatch.AcceptButton}
                        fontSize={28}
                        sound={Sounds.cancel}
                        onClick={() => goToPreviousMenu(MenuPanels.Stats)}
                    >
                        {Localization.instance.get('PurchaseFailedDialogAccept')}
                    </MenuButton>
                </Row>
            </Column>
        </Container>
    );
};
